import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import Banner from './banner';
import Text from '../text/text';
import Row from '../grid/row';
import Col from '../grid/column';
import Button from '../button/button';
import Box from '../box/box';
import Modal from '../modal/modal';
import Switch from '../switch/switch';

const defaultPreferences = {
    functional: true,
    analytics: false,
};

const measurementId = 'G-GXEMMS5P1Z';
const isProd = process.env.REACT_APP_CLUSTER === 'avocado';

const CookieBanner = () => {
    const [showModal, setShowModal] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    const [preferences, setPreferences] = useState(defaultPreferences);

    const initializeGA = () => {
        const ignoreAnalytics =
            JSON.parse(localStorage.getItem('ignoreAnalytics')) || false;
        if (isProd && !ignoreAnalytics) {
            ReactGA.initialize(measurementId);
        }
    };

    const onAcceptSelection = () => {
        localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
        setShowModal(false);
        setShowBanner(false);
        if (preferences.analytics) {
            initializeGA();
        }
    };

    const onAcceptAll = () => {
        const allSelected = { ...defaultPreferences, analytics: true };
        localStorage.setItem('cookiePreferences', JSON.stringify(allSelected));
        setPreferences(allSelected);
        setShowModal(false);
        setShowBanner(false);
        initializeGA();
    };

    useEffect(() => {
        const storedPreferences = JSON.parse(
            localStorage.getItem('cookiePreferences'),
        );
        if (storedPreferences) {
            setPreferences(storedPreferences);
            if (storedPreferences.analytics) {
                initializeGA();
            }
        } else {
            setShowBanner(true);
        }
    }, []);

    if (!showBanner) return null;

    return (
        <>
            <Banner position="bottom" color={['purple', 'darkest']}>
                <Row justifyContent="space-between">
                    <Col spanXs={12} spanMd={8}>
                        <Text size="small" color={['gray', 'white']}>
                            We use cookies from third party services to offer
                            you a better experience. Read about how we use
                            cookies and how you can control them by clicking
                            Privacy Preferences.
                        </Text>
                    </Col>

                    <Col spanXs={5} spanMd={4}>
                        <Box>
                            <Row justifyContent="flex-end" gap="base">
                                <Col span="auto">
                                    <Button
                                        size="small"
                                        theme="dark"
                                        onClick={() => setShowModal(true)}
                                    >
                                        Privacy Preferences
                                    </Button>
                                </Col>
                                <Col span="auto">
                                    <Button onClick={onAcceptAll} size="small">
                                        Accept All
                                    </Button>
                                </Col>
                            </Row>
                        </Box>
                    </Col>
                </Row>
            </Banner>
            {showModal && (
                <Modal
                    title="Privacy Preferences"
                    actionItems={
                        <Row>
                            <Col>
                                <Button
                                    onClick={() => setShowModal(false)}
                                    theme="outline"
                                >
                                    Close
                                </Button>
                            </Col>
                            <Col>
                                <Button onClick={onAcceptSelection}>
                                    Accept Selection
                                </Button>
                            </Col>
                        </Row>
                    }
                    preventClosing
                >
                    <Text color={['gray', 'base']}>
                        When you visit any website, it may store or retrieve
                        information through your browser, usually in the form of
                        cookies. Since we respect your right to privacy, you can
                        choose not to permit data collection from certain types
                        of services. However, not allowing these services may
                        impact your experience and what we are able to offer
                        you.
                    </Text>
                    <Box margin={['large', 0, 'small']}>
                        <Row justifyContent="space-between" alignItems="center">
                            <Col>
                                <Text size="larger" weight="bold">
                                    Functional
                                </Text>
                            </Col>
                            <Col span="auto">
                                <Switch
                                    isChecked={preferences.functional}
                                    isDisabled
                                    labels={['Reject', 'Accept']}
                                    onChange={() => null}
                                />
                            </Col>
                        </Row>
                    </Box>
                    <Box margin={[0, 0, 'large']}>
                        <Text color={['gray', 'base']}>
                            These cookies enable core website functionality and
                            personalization, such as allowing you to log in
                            securely and remembering your preferences or
                            settings. They help ensure the site works as
                            intended and provide a consistent experience across
                            visits. Because they are essential to the website’s
                            operation, they are accepted by default.
                        </Text>
                    </Box>
                    <Box margin={['large', 0, 'small']}>
                        <Row justifyContent="space-between" alignItems="center">
                            <Col>
                                <Text size="larger" weight="bold">
                                    Analytics
                                </Text>
                            </Col>
                            <Col span="auto">
                                <Switch
                                    theme="equal"
                                    isChecked={preferences.analytics}
                                    labels={['Reject', 'Accept']}
                                    onChange={() => {
                                        setPreferences({
                                            ...preferences,
                                            analytics: !preferences.analytics,
                                        });
                                    }}
                                />
                            </Col>
                        </Row>
                    </Box>
                    <Box>
                        <Text color={['gray', 'base']}>
                            These optional cookies help us understand how
                            visitors use our website by collecting information
                            such as page views, traffic sources, and user
                            interactions. They are set by third-party analytics
                            providers and allow us to analyze trends and improve
                            the performance and content of our site. If you do
                            not allow these cookies, we will have less insight
                            into how our website is used.
                        </Text>
                    </Box>
                    <Box margin={['large', 0, 0]}>
                        <Text color={['gray', 'base']}>
                            For more information, please refer to our corporate{' '}
                            <a href="https://wearesilverbullet.com/privacy-policy/">
                                Privacy Policy
                            </a>
                            .
                        </Text>
                    </Box>
                </Modal>
            )}
        </>
    );
};

export default CookieBanner;
